import { Check, Copy, Lock } from '@tamagui/lucide-icons'
import { interaction, SizableText, XStack, YStack } from '@mythical/ui'
import { PromoCode } from 'app/blocks/promo-code'
import React from 'react'

import Section from 'app/components/structure/Section'
import { Tag } from 'app/components/primitive/Tag/Tag'
import { Button } from 'app/components/primitive/Button'
import { IconButton } from 'app/components/primitive/IconButton'
import { copyToClipboard } from 'app/lib/copy-to-clipboard'
import { useStores } from 'app/store'
import { observer } from 'mobx-react-lite'

interface Props {
  block: PromoCode
  slug?: string
}

type PromoCardKind = 'discount' | 'password'
type PromoCardId = 'code' | 'password'

type PromoCard = {
  id: PromoCardId
  kind: PromoCardKind
  chipText: string
  title: string
  value: string
}

function resolveCardText(
  rawTitle: string | undefined,
  defaultChipText: string,
  defaultBodyText: string,
) {
  const value = rawTitle?.trim() || ''

  if (!value) {
    return {
      chipText: defaultChipText,
      bodyText: defaultBodyText,
    }
  }

  if (value.length <= 20) {
    return {
      chipText: value,
      bodyText: defaultBodyText,
    }
  }

  return {
    chipText: defaultChipText,
    bodyText: defaultBodyText,
  }
}

export const PromoCodeBlock: React.FC<Props> = observer(({ block, slug }) => {
  const { upgradeModalState, userStore } = useStores()
  const { codeTitle, passwordTitle, password, code } = block
  const [copiedCard, setCopiedCard] = React.useState<PromoCardId | null>(null)

  if (!code) return null

  const codeText = resolveCardText(
    codeTitle,
    '15% Off',
    "This month's discount code is",
  )
  const passwordText = resolveCardText(
    passwordTitle,
    'All Access',
    'Password for exclusive items',
  )

  const cards: PromoCard[] = [
    {
      id: 'code',
      kind: 'discount',
      chipText: codeText.chipText,
      title: codeText.bodyText,
      value: code || '',
    },
  ]

  if (passwordTitle || password || slug === 'merch') {
    cards.push({
      id: 'password',
      kind: 'password',
      chipText: passwordText.chipText,
      title: passwordText.bodyText,
      value: password || '',
    })
  }

  const handleUpgrade = () => {
    upgradeModalState.openUpsellModal()
  }

  const onCopyPress = async (card: PromoCard) => {
    const copied = await copyToClipboard(card.value)
    if (!copied) return

    setCopiedCard(card.id)
    setTimeout(
      () => setCopiedCard((prev) => (prev === card.id ? null : prev)),
      1400,
    )
  }

  const canMemberfulLink = userStore?.canMemberfulLink ?? false

  return (
    <Section paddedX gap="$3" w="100%" $gtXxxs={{ fd: 'row' }}>
      {cards.map((card) => {
        const isCopied = copiedCard === card.id
        const isLocked = card.kind === 'password' && !card.value
        return (
          <YStack
            key={card.id}
            flex={1}
            bw={2}
            jc="center"
            ai="center"
            bg={isLocked ? '$bgPrimary' : '$bgTertiary'}
            boc={isLocked ? '$borderSecondary' : '$transparent'}
            br="$5"
            px="$3.5"
            py="$5"
            gap="$3"
          >
            <YStack gap="$3" ai="center">
              <Tag
                variant={card.kind === 'discount' ? 'primary' : 'white'}
                displaySize="md"
              >
                {card.chipText}
              </Tag>
              <SizableText
                ff="$title"
                size="$m"
                ta="center"
                color={isLocked ? '$fgSecondary' : '$fgPrimary'}
                $gtSm={{ size: '$xl' }}
              >
                {card.title}
              </SizableText>
            </YStack>

            {isLocked && canMemberfulLink && (
              <Button
                onPress={handleUpgrade}
                icon={<Lock size={18} color="$fgSecondary" />}
                size="$3"
                scaleSpace={0.3}
                theme="transparent"
                color="$fgSecondary"
                {...interaction.opacity}
              >
                Unlock
              </Button>
            )}
            {!isLocked && (
              <XStack ai="center" jc="center" gap="$1.5">
                <SizableText
                  size="$m"
                  color="$fgSecondary"
                  userSelect="all"
                  ta="center"
                  $gtSm={{ size: '$l' }}
                >
                  {card.value}
                </SizableText>
                <IconButton size="$2" onPress={() => onCopyPress(card)}>
                  {isCopied ? (
                    <Check size={18} color="$fgSecondary" />
                  ) : (
                    <Copy size={18} color="$fgSecondary" />
                  )}
                </IconButton>
              </XStack>
            )}
          </YStack>
        )
      })}
    </Section>
  )
})
